/**
 * Resolves which badges a listing shows.
 *
 * Pure: no React, no SDK. `now` is injectable so the 30 day boundary is testable
 * from both sides — badgesForListing defaults it to the current time at this
 * module's public boundary, but the predicate that uses it never reads the clock
 * itself. Each badge is read from the source where its truth actually lives —
 * awards from the provider, 'popular' from listing metadata written by a job, and
 * 'new' computed from a timestamp so nothing has to write or expire it.
 */
import { listingBadges, NEW_LISTING_DAYS, BADGE_SOURCES } from '../config/configListingBadges';
import { awards } from '../config/configAwards';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Awards held by a listing's provider, in registry order.
 *
 * @param {Object} [author] API entity: user
 * @returns {Array<Object>} award configs
 */
export const awardsForAuthor = author => {
  const profile = author?.attributes?.profile;
  // A Console user field lands under profile.metadata when its scope is 'metadata' and
  // under profile.publicData when it is 'public' (see pickCustomFieldProps in
  // util/fieldHelpers.js). Metadata is the correct scope for an award, because only the
  // operator can write it - a provider must not be able to award themselves. Public is
  // read as a fallback so the badge still works if the field is configured that way.
  const held = profile?.metadata?.awards ?? profile?.publicData?.awards;
  const heldSlugs = Array.isArray(held) ? held : [];
  return awards.filter(award => heldSlugs.includes(award.slug));
};

/**
 * True while a published listing is inside the "new" window.
 *
 * There is no publishedAt attribute in the Marketplace API, so createdAt is the
 * fallback. It is only a fallback: a listing can sit in draft or pendingApproval
 * for weeks, and metadata.publishedAt (stamped by the metrics job) is the honest
 * answer when it exists.
 */
const isNewListing = (listing, now) => {
  const { state, createdAt, metadata } = listing?.attributes || {};

  if (state !== 'published') {
    return false;
  }

  const publishedAt = metadata?.publishedAt ?? createdAt;
  const published = publishedAt ? new Date(publishedAt) : null;

  if (!published || Number.isNaN(published.getTime())) {
    return false;
  }

  const age = now.getTime() - published.getTime();
  return age >= 0 && age < NEW_LISTING_DAYS * DAY_IN_MS;
};

// Predicates for badges with source: 'computed', keyed by slug.
const COMPUTED = {
  new: isNewListing,
};

/**
 * Every badge a listing qualifies for, in registry (priority) order.
 *
 * Returns the full ranked list rather than one badge: the cap is a rendering
 * decision, so the listing page could show two without touching this function.
 *
 * @param {Object} listing API entity: listing
 * @param {Object} [author] API entity: the listing's provider
 * @param {Object} [options]
 * @param {Date} [options.now] injected for testability
 * @returns {Array<Object>} badge configs, possibly empty
 */
export const badgesForListing = (listing, author, options = {}) => {
  const { now = new Date() } = options;

  const stored = listing?.attributes?.metadata?.badges;
  const storedSlugs = Array.isArray(stored) ? stored : [];
  const hasAward = awardsForAuthor(author).length > 0;

  return listingBadges.filter(badge => {
    switch (badge.source) {
      case BADGE_SOURCES.AUTHOR:
        return hasAward;
      case BADGE_SOURCES.METADATA:
        return storedSlugs.includes(badge.slug);
      case BADGE_SOURCES.COMPUTED: {
        const predicate = COMPUTED[badge.slug];
        return predicate ? predicate(listing, now) : false;
      }
      default:
        return false;
    }
  });
};
